import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as env from "shared/env";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import moment from "moment";
import { TaskCard } from "./task-card";
import { Task } from "model/task";
import { getFilteredTasks, getFilteredTasksByAgentId, getUser } from "redux/selector";
import { getTasksPageFiltersCount, getTasksPageFilters } from "redux/selector";
import { setTasksPageFilters } from "redux/slice/tasks-page";
import { AddEditTaskDialog } from "component/shared/add-edit-task-dialog";
import Fuse from "fuse.js";
import { styles } from "./styles";
import { FilterDialog } from "./filter-dialog";
import { SubsectionHeader } from "./subsection-header";
import queryString from "query-string";
import { TasksPageFilters } from "model/tasks-page-filters";
import { TaskFilterType } from "type/task-filter";
import { TasksPageFiltersState } from "redux/slice/tasks-page";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";

interface TaskByDate {
	[date: string]: Task[];
}

interface TaskByFirstLetter {
	[letter: string]: Task[];
}

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const Component: React.FC<Props> = ({ history, location, classes = {} }) => {
	const dispatch = useAppThunkDispatch();
	
	// Redux state
	const user = useSelector(getUser);

	const [tasks, setTasks] = useState<Task[]>([]);

	const filteredTasks = useSelector(getFilteredTasks);
	const filteredTaskByAgentId = useSelector((state: RootState) => getFilteredTasksByAgentId(state, user.agentId));

useEffect(() => {
	if (user?.admin) {
		setTasks(filteredTasks);
	} else {
		setTasks(filteredTaskByAgentId);
	}
}, [user, filteredTasks, filteredTaskByAgentId]);

	const filterCount = useSelector(getTasksPageFiltersCount);
	const filters = useSelector(getTasksPageFilters);
	const loading = useSelector((state: RootState) => state.tasks.loading);

	// Local state
	const [editTaskDialogIsOpen, setEditTaskDialogIsOpen] = useState(false);
	const [tasksFilterDialogIsOpen, setTasksFilterDialogIsOpen] = useState(false);
	const [task, setTask] = useState<Task | null>(null);
	const [term, setTerm] = useState("");
	const [sortTypeId, setSortTypeId] = useState(1);

	// Get leads and agents for enriched search
	const leads = useSelector((state: RootState) => state.leads.byId);
	const agents = useSelector((state: RootState) => state.agents.byId);

	// Create enriched tasks with lead and agent information for searching
	const enrichedTasks = useMemo(() => {
		return tasks.map(task => ({
			...task,
			leadName: task.leadId ? leads[task.leadId]?.name : "",
			leadEmail: task.leadId ? leads[task.leadId]?.email : "",
			agentFirstName: task.agentId ? agents[task.agentId]?.firstName : "",
			agentLastName: task.agentId ? agents[task.agentId]?.lastName : "",
			agentEmail: task.agentId ? agents[task.agentId]?.email : "",
		}));
	}, [tasks, leads, agents]);

	// Memoized fuse instance
	const fuse = useMemo(() => {
		const options: Fuse.IFuseOptions<any> = {
			shouldSort: true,
			threshold: 0.5,
			location: 0,
			distance: 100,
			minMatchCharLength: 1,
			keys: ["title", "description", "leadName", "leadEmail", "agentFirstName", "agentLastName", "agentEmail"],
		};
		return new Fuse(enrichedTasks, options);
	}, [enrichedTasks]);

	// Memoized helper functions
	const getTasksByDate = useCallback((tasks: Task[]) => {
		let tasksByDate: TaskByDate = {
			0: [], // Overdue tasks
		};
		tasks.forEach(task => {
			const date = task.endOn;
			// Group all overdue tasks together
			if (date < moment()) {
				tasksByDate[0].push(task);
			} else {
				// Group all other tasks by date
				const dateKey = date.format("YYYYMMDD");
				if (tasksByDate[dateKey]) {
					tasksByDate[dateKey].push(task);
				} else {
					tasksByDate[dateKey] = [task];
				}
			}
		});
		return tasksByDate;
	}, []);

	const getTasksByFirstLetter = useCallback((tasks: Task[]) => {
		let tasksByFirstLetter: TaskByFirstLetter = {};
		tasks.forEach(task => {
			const firstLetter = task.title[0].toUpperCase();
			const charCode = firstLetter.charCodeAt(0);

			// Is alphanumeric
			if (
				("0".charCodeAt(0) <= charCode && charCode <= "9".charCodeAt(0)) ||
				("A".charCodeAt(0) <= charCode && charCode <= "Z".charCodeAt(0))
			) {
				if (tasksByFirstLetter[firstLetter]) {
					tasksByFirstLetter[firstLetter].push(task);
				} else {
					tasksByFirstLetter[firstLetter] = [task];
				}
			} else {
				/*is non-numerical*/
				if (tasksByFirstLetter["#"]) {
					tasksByFirstLetter["#"].push(task);
				} else {
					tasksByFirstLetter["#"] = [task];
				}
			}
		});
		return tasksByFirstLetter;
	}, []);

	const getFiltersFromParams = useCallback((): TasksPageFilters => {
		const query = queryString.parse(location.search);
		const filters: TasksPageFiltersState = {};
		Object.keys(query).forEach((name) => {
			let value: any = query[name];
			const type = TaskFilterType.getById(name);
			switch(type) {
				case TaskFilterType.TYPE: {
					if (Array.isArray(value)) {
						filters.type = value.map((val: string) => parseInt(val));
					} else {
						filters.type = [parseInt(value)];
					}
					break;
				}
				case TaskFilterType.STATUS: {
					if (Array.isArray(value)) {
						filters.status = value.map((val: string) => parseInt(val));
					} else {
						filters.status = [parseInt(value)];
					}
					break;
				}
				case TaskFilterType.PRIORITY: {
					if (Array.isArray(value)) {
						filters.priority = value.map((val: string) => val === "true");
					} else {
						filters.priority = value === "true" ? [true] : undefined;
					}
					break;
				}
				case TaskFilterType.TODAY: {
					filters.today = value === "true";
					break;
				}
				case TaskFilterType.LEAD: {
					filters.lead = parseInt(value);
					break;
				}
			}
		});
		return filters;
	}, [location.search]);

	const getOtherQueryParams = useCallback(() => {
		const query = queryString.parse(location.search);
		const queryParams: {
			[key: string]: any
		} = {};
		Object.keys(query).forEach((key) => {
			if (!TaskFilterType.getById(key)) {
				queryParams[key] = query[key];
			}
		});
		return queryParams;
	}, [location.search]);

	const updateUrl = useCallback((filters: TasksPageFilters) => {
		let queryParams: {
			[key: string]: any
		} = {
			...getOtherQueryParams(),
		};
		Object.keys(filters).forEach((name) => {
			// @ts-ignore
			const value = filters[name];
			queryParams[name] = value;
		});
		const query = queryString.stringify(queryParams);
		history.push({search: query});
	}, [history, getOtherQueryParams]);

	// Effects
	useEffect(() => {
		const filtersFromParams = getFiltersFromParams();
		if (Object.keys(filtersFromParams).length) {
			dispatch(setTasksPageFilters(filtersFromParams));
		}
	}, []); // Only run on mount

	useEffect(() => {
		updateUrl(filters);
	}, [filters, updateUrl]);

	// Event handlers
	const showTasksFilterDialog = useCallback(() =>
		setTasksFilterDialogIsOpen(true), []);

	const hideTasksFilterDialog = useCallback(() =>
		setTasksFilterDialogIsOpen(false), []);

	const onTermChange = useCallback((
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	) => setTerm(event.target.value), []);

	const openEditTaskDialog = useCallback((task: Task | null = null) => {
		setEditTaskDialogIsOpen(true);
		setTask(task);
	}, []);

	const closeEditTaskDialog = useCallback(() => {
		setEditTaskDialogIsOpen(false);
		setTask(null);
	}, []);

	// Render functions
	const renderNoTasksMessage = useCallback(() => {
		return (
			<Mui.Grid container item justifyContent="center" style={{ marginTop: 45 }}>
				<Mui.Typography variant="h3" align="center">
					You have no tasks at the moment. Click the Add New button to create a
					new task.
				</Mui.Typography>
			</Mui.Grid>
		);
	}, []);

	const renderSectionBody = useCallback((
		subHeader: string,
		tasks: Task[],
		index: number,
		isOverdue: boolean = false
	) => (
		<Mui.Grid container item key={index}>
			<Mui.Grid item xs={12}>
				<SubsectionHeader label={subHeader} isOverdue={isOverdue} />
			</Mui.Grid>
			<Mui.Grid item xs={12}>
				{tasks.map((task, index) => {
					return (
						<TaskCard
							task={task}
							key={index}
							onClick={() => openEditTaskDialog(task)}
						/>
					);
				})}
			</Mui.Grid>
		</Mui.Grid>
	), [openEditTaskDialog]);

	const renderSectionsByDueDate = useCallback(() => {
		let filteredTasks = tasks;
		if (term) {
			filteredTasks = fuse.search(term).map(result => result.item);
		}
		const tasksByDate = getTasksByDate(filteredTasks);

		// Group tasks into three categories
		const overdueTasks: Task[] = [];
		const todayTasks: Task[] = [];
		const upcomingTasks: Task[] = [];

		Object.keys(tasksByDate)
			.sort()
			.forEach((date) => {
				const tasksForDate = tasksByDate[date] || [];
				if (!tasksForDate.length) return;

				const dateTimestamp = tasksForDate[0].endOn;

				if (date === "0") {
					// Overdue
					overdueTasks.push(...tasksForDate);
				} else if (
					dateTimestamp.format("YYYYMMDD") === moment().format("YYYYMMDD")
				) {
					// Today
					todayTasks.push(...tasksForDate);
				} else if (
					dateTimestamp.format("YYYYMMDD") !== moment().format("YYYYMMDD") &&
					dateTimestamp.unix() - moment().unix() > 0
				) {
					// Upcoming
					upcomingTasks.push(...tasksForDate);
				}
			});

		return (
			<>
				{overdueTasks.length > 0 && renderSectionBody("Overdue", overdueTasks, 0, true)}
				{todayTasks.length > 0 && renderSectionBody("Today", todayTasks, 1, false)}
				{upcomingTasks.length > 0 && renderSectionBody("Upcoming", upcomingTasks, 2, false)}
			</>
		);
	}, [tasks, term, fuse, getTasksByDate, renderSectionBody]);

	const renderSectionsAlphabetically = useCallback(() => {
		let filteredTasks = tasks;
		if (term) {
			filteredTasks = fuse.search(term).map(result => result.item);
		}
		const sortedTasks = getTasksByFirstLetter(filteredTasks);
		return Object.keys(sortedTasks).map((letter, index) =>
			renderSectionBody(letter, sortedTasks[letter], index)
		);
	}, [tasks, term, fuse, getTasksByFirstLetter, renderSectionBody]);

	const title = "Tasks";

	return (
		<DashboardLayout
			permitted={user && user.permissions.crmTasks}
			title={title}
			header={
				<Mui.Grid container justifyContent="space-between" alignItems="center">
					<Mui.Grid item>
						<Mui.Typography variant="h1">
							{title}
						</Mui.Typography>
					</Mui.Grid>
				</Mui.Grid>
			}
		>
			<FilterDialog
				open={tasksFilterDialogIsOpen}
				onClose={hideTasksFilterDialog}
				count={tasks.length}
			/>
			<AddEditTaskDialog
				open={editTaskDialogIsOpen}
				onClose={closeEditTaskDialog}
				task={task}
			/>
			<div className={classes.headerContainer}>
				<div className={classes.leftSection}>
					<div className={classes.searchBar}>
						<Icons.Search className={classes.searchIcon} size={24} />
						<Mui.InputBase
							placeholder="Search Tasks"
							fullWidth
							onChange={onTermChange}
						/>
					</div>
					<Mui.Button
						onClick={showTasksFilterDialog}
						className={`${classes.button} ${classes.filterButton}`}
					>
						<span className={classes.filterIcon}>
							<Icons.Filter size={24} />
						</span>
						<span className={classes.filterText}>
							Filters {filterCount ? `(${filterCount})` : ""}
						</span>
					</Mui.Button>
					<Mui.Button
						onClick={() => setSortTypeId(sortTypeId === 1 ? 2 : 1)}
						className={`${classes.button} ${classes.sortButton}`}
					>
						{sortTypeId === 1
						? "Sort By Due Date"
						: "Sort Alphabetically"}
						<Icons.ChevronDown className={classes.sortIcon} size={24} />
					</Mui.Button>
				</div>
				<div className={classes.rightSection}>
					<Mui.Button
						onClick={() => openEditTaskDialog()}
						className={`${classes.button} ${classes.addButton}`}
					>
						Add Task
					</Mui.Button>
				</div>
			</div>
			<Mui.Grid container spacing={3} direction="row">
				{!loading && !tasks.length && renderNoTasksMessage()}
				{!!tasks.length && sortTypeId === 1 && renderSectionsByDueDate()}
				{!!tasks.length && sortTypeId === 2 && renderSectionsAlphabetically()}
			</Mui.Grid>
		</DashboardLayout>
	);
};

export const TasksPage = withStyles(Component, styles);
